import type { ReactElement } from 'react';
import type { ExtensionListItem } from '@kirin/api-client/contracts/extensions';
import {
  VerifiedPublisherIcon,
  IDEBadge,
} from '@/components/shared/extensions';
import { Link } from 'react-router-dom';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';

interface CellProps {
  row: ExtensionListItem;
}

export function NameCell({ row }: CellProps) {
  const name = row.displayName ?? row.extensionName;
  return (
    // KIR-5640: one line with an ellipsis and the full name in the tooltip;
    // `break-words` split names without spaces mid-word ("serve / r-…").
    <span className="font-medium block truncate" title={name}>
      {name}
    </span>
  );
}

export function PublisherCell({ row }: CellProps) {
  return (
    <div className="flex items-center gap-1.5">
      <VerifiedPublisherIcon isVerified={row.isVerified} />
      <span
        className="min-w-0 truncate text-muted-foreground"
        title={row.publisher}
      >
        {row.publisher}
      </span>
    </div>
  );
}

export function IDECell({ row }: CellProps) {
  return <IDEBadge sources={row.sources} />;
}

export function FirstSeenCell({ row }: CellProps) {
  return <RelativeTimeWithTooltip date={row.firstSeenAt} />;
}

export function LastSeenCell({ row }: CellProps) {
  return <RelativeTimeWithTooltip date={row.lastSeenAt} />;
}

export function ScanResultLinkCell({ row }: CellProps): ReactElement | null {
  if (row.securitySummary == null) {
    return null;
  }

  const version = row.versions[0]?.version;
  const params = new URLSearchParams();
  if (version) {
    params.set('version', version);
  }
  const query = params.toString();

  return (
    <Link
      to={`/dashboard/inventory/extensions/${encodeURIComponent(row.extensionId)}/scan-result${query ? `?${query}` : ''}`}
      className="text-primary text-sm hover:underline"
    >
      View scan result →
    </Link>
  );
}
